import React, { useContext } from 'react';
import { Link } from "react-router-dom";
import cx from "classnames";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import html2canvas from 'html2canvas';
import { renderToStaticMarkup } from 'react-dom/server';
import { SpecialtyDetailFootnote } from '@app/projects/results/adequacy';
import { MultiMapExportRouteState } from '@app/projects/network-assessment/multi-map-export/interfaces';
import { formatDateTime } from '@app/shared/formatDateTime';
import { truncateStringWithEllipsis } from '@app/shared/utility/truncateStringWithEllipsis';
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

const MAX_MEMBERSHIP_TYPE_LENGTH = 50;
const MAX_PROJECT_NAME_LENGTH = 75;

interface MapExportProps {
  isLoading: boolean;
  launchContext: MultiMapExportRouteState;
  membershipType: string | null;
  projectId: number;
  projectRunConfigurationId: number;
  runConfigurationDateCreated: Date;
  projectName: string;
  specialtyDetailInfo: SpecialtyDetailFootnote;
}

const MapExport: React.FC<MapExportProps> = ({ isLoading, launchContext, membershipType, projectId, projectRunConfigurationId, runConfigurationDateCreated, projectName, specialtyDetailInfo }) => {
  const ffContext = useContext(FeatureFlagContext);
  const multiMapExportEnabled = ffContext.enabledFeatureFlags.has(Features.VOLUME_MAP_EXPORTS_COUNTY);
  const showMembershipType = ffContext.enabledFeatureFlags.has(Features.DUAL_MEMBERSHIP_FOR_MAPS) && !!membershipType;

  const exportMap = (showDetails: boolean) => {
    const titleDiv = getTitleDiv();
    const exportNameOriginal = `Network Coverage Map ${specialtyDetailInfo.geographyName} ${specialtyDetailInfo.specialtyGroupName} ${projectName}${showDetails ? ' with Details' : ''}`;
    const exportName =  exportNameOriginal.replace(/\./g, "_");
    const selector = '.network-coverage-map__map-container';
    const exportDiv = document.querySelector(selector) as HTMLElement;

    html2canvas(exportDiv as HTMLElement, {
      ignoreElements: function (element) {
        return element.classList.contains(
          "mapboxgl-ctrl-group") ||
          (!showDetails && (
              element.classList.contains("provider-member-mapping__map-details-container") ||
              element.classList.contains("network-coverage-map__map-details-county-box")
            )
          );
      },
      onclone: async (clonedDoc) => {
        const clonedExportDiv = clonedDoc.querySelector(selector) as HTMLElement;

        if (clonedExportDiv) {
          clonedExportDiv.className = 'network-coverage-map__map-export-canvas-container';
          clonedExportDiv.insertBefore(titleDiv, clonedExportDiv.firstChild);

          const logo = clonedExportDiv.querySelector<HTMLImageElement>('#logo');

          if (logo) {
            await logo.decode().catch((): void => undefined);
          }
        }
      },
      removeContainer: true,
      scale: 2

    }).then(exportCanvas => {
      const imgData = exportCanvas.toDataURL("image/png");

      const link = document.createElement('a');
      link.download = exportName;
      link.href = imgData;
      link.click();
    });
  };

  const getTitleDiv = () => {
    const output = document.createElement('a');
    output.innerHTML =
      renderToStaticMarkup(
        <div className='title-div'>
          <div className="text-container">
            <span className="title-text">Network Coverage Map of {specialtyDetailInfo.specialtyGroupName} for {specialtyDetailInfo.geographyName}</span>
            <div className="info-div">
              <span className="network-coverage-map__info-text"><strong>QES Project Name:</strong> {truncateStringWithEllipsis(projectName, MAX_PROJECT_NAME_LENGTH)}</span>
              {showMembershipType && <span className="network-coverage-map__info-text"><strong>Membership Type:</strong> {truncateStringWithEllipsis(membershipType, MAX_MEMBERSHIP_TYPE_LENGTH)}</span>}
            </div>
            <div className="info-div">
              <span className="date-text"><strong>Map Created on:</strong> {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              <span className="date-text"><strong>Results As Of:</strong> {formatDateTime(runConfigurationDateCreated)}</span>
            </div>
          </div>
          <img className="title-image" src={`${window.customUIBaseUrl}/images/QuestAnalyticsLogo-R.png`} alt="Quest Analytics" role="img" id="logo" />
        </div>
      );

    return output;
  }

  const getExportMapImageDisplayText = () => {
    if (!multiMapExportEnabled) { return "Export map image"; }

    return <span>
            <span className="new-action-menu-item__title">Export Map Image</span>
            <span className="new-action-menu-item__caption">Downloads the current map view as a .png</span>
          </span>;
  }

  const getExportMapImageDisplayWithDetailsText = () => {
    if (!multiMapExportEnabled) { return "Export map image with details"; }

    return <span>
            <span className="new-action-menu-item__title">Export Map Image with Details</span>
            <span className="new-action-menu-item__caption">Includes the Map Details box</span>
          </span>;
  }

  return (
    // The Export control itself isn't flag-gated, so the grouped styling hangs off a modifier.
    <div
      className={cx("provider-member-mapping__export-dropdown", {
        "provider-member-mapping__export-dropdown--grouped": multiMapExportEnabled,
      })}
      data-html2canvas-ignore="true"
    >
      <NewActionMenu
        alignRight={true}
        buttonText="Export"
        testId="provider-member-mapping__export-dropdown"
        buttonClassNames="qes-button-primary"
        dropup={false}
        loading={isLoading}>
        {multiMapExportEnabled && (
          <div
            className="provider-member-mapping__export-group-header"
            data-testid="provider-member-mapping__export-dropdown-single-map-group"
          >
            Single Map Export
          </div>
        )}
        <NewActionMenuItem
          display={getExportMapImageDisplayText()}
          isSelected={false}
          key={1}
          onClick={() => exportMap(false)}
          testId="provider-member-mapping__export-dropdown-option1"
        />
        <NewActionMenuItem
          display={getExportMapImageDisplayWithDetailsText()}
          isSelected={false}
          key={2}
          onClick={() => exportMap(true)}
          testId="provider-member-mapping__export-dropdown-option2"
        />
        {multiMapExportEnabled && (
          <>
            <div
              className="provider-member-mapping__export-group-divider"
              data-testid="provider-member-mapping__export-dropdown-group-divider"
            />
            <div
              className="provider-member-mapping__export-group-header"
              data-testid="provider-member-mapping__export-dropdown-multi-map-group"
            >
              Multi-Map Export
            </div>
            <Link
              className="new-action-menu-item qes-button"
              data-testid="provider-member-mapping__export-dropdown-option3"
              key={3}
              to={{
                pathname: `/projects/${projectId}/${projectRunConfigurationId}/map-exporter`,
                state: launchContext,
              }}
            >
              <span className="new-action-menu-item__title">Create Multiple Maps</span>
              <span className="new-action-menu-item__caption">Create a .zip file of multiple maps at once from a set of criteria</span>
            </Link>
          </>
        )}
      </NewActionMenu>
    </div>
  );
};

export default MapExport;
